import { useEffect, useMemo, useRef, useState } from "react";
import { errorText, opsApi, type BinOption, type ScannedBin } from "@/lib/warehouseOps";

/** Số dòng gợi ý tối đa; nhiều hơn thì cuộn trên màn hình nhỏ chỉ thêm rối. */
const MAX_SUGGESTIONS = 30;

/**
 * Ô nhập mã vị trí — cửa vào của mọi thao tác trong kho.
 *
 * Máy quét cầm tay gõ mã rồi tự bắn Enter, nên ô này xử lý Enter là chính; nút
 * bấm chỉ để dùng khi phải gõ tay vì tem mờ.
 *
 * Không tự đoán ô gần đúng: quét ra ô nào thì làm ở đúng ô đó, sai một ký tự là
 * hàng vào nhầm kệ.
 */
export default function BinScanner({
  warehouseId,
  token,
  label = "Quét mã vị trí",
  hint,
  expectedCode,
  searchable = false,
  onScanned,
}: {
  warehouseId: string;
  token?: string;
  label?: string;
  hint?: string;
  /** Chỉ chấp nhận đúng ô này; quét ô khác thì báo ngay tại máy. */
  expectedCode?: string;
  /**
   * Gõ vài ký tự thì hiện danh sách ô khớp để chạm chọn, thay vì phải gõ trọn
   * mã. Máy quét bắn Enter vẫn hoạt động y như cũ. Dùng ở bước cất hàng, nơi
   * tem kệ hay mờ và người dùng biết mình muốn cất vào khu nào.
   */
  searchable?: boolean;
  onScanned: (bin: ScannedBin) => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<BinOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, [warehouseId, expectedCode]);

  // Danh sách ô tải một lần cho mỗi kho; lọc ở máy vì tối đa vài trăm ô.
  useEffect(() => {
    if (!searchable || !warehouseId) return;

    let cancelled = false;
    setOptionsLoading(true);

    opsApi.bins(warehouseId, token)
      .then((res) => { if (!cancelled) setOptions(res.data ?? []); })
      .catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setOptionsLoading(false); });

    return () => { cancelled = true; };
  }, [searchable, warehouseId, token]);

  const suggestions = useMemo(() => {
    if (!searchable) return [];
    const term = code.trim().toUpperCase();
    const pool = term === ""
      ? options
      : options.filter((b) =>
        b.code.toUpperCase().includes(term)
        || b.name.toUpperCase().includes(term)
        || b.zoneCode.toUpperCase().includes(term)
        || b.zoneName.toUpperCase().includes(term));
    // Khớp từ đầu mã đứng trước khớp giữa chuỗi.
    return [...pool]
      .sort((a, b) => Number(b.code.toUpperCase().startsWith(term)) - Number(a.code.toUpperCase().startsWith(term)))
      .slice(0, MAX_SUGGESTIONS);
  }, [searchable, options, code]);

  const submit = async (raw: string) => {
    const value = raw.trim();
    if (!value || busy) return;

    setBusy(true);
    setError("");

    try {
      const res = await opsApi.scanBin(warehouseId, value, token);
      const bin = res.data;

      if (expectedCode && bin.code !== expectedCode) {
        setError(`Ô vừa quét là ${bin.code}, cần quét ô ${expectedCode}.`);
        setCode("");
        return;
      }

      if (bin.blocked) {
        setError(`Ô ${bin.code} đang ở trạng thái "${bin.status}", không thao tác được.`);
        setCode("");
        return;
      }

      setCode("");
      onScanned(bin);
    } catch (e) {
      setError(errorText(e, "Không đọc được mã vị trí."));
    } finally {
      setBusy(false);
      setOpen(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}</label>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => { setCode(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          // Chờ một nhịp để chạm vào dòng gợi ý kịp chạy trước khi danh sách đóng.
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit(code);
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder={searchable ? "Bắn mã hoặc gõ để tìm ô..." : "Bắn mã trên kệ..."}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          className="flex-1 min-w-0 px-3 py-3 text-base font-mono border-2 border-slate-200 rounded-lg focus:outline-none focus:border-brand-500"
        />
        <button
          onClick={() => void submit(code)}
          disabled={busy || !code.trim()}
          className="shrink-0 px-4 rounded-lg bg-brand-600 text-white font-medium disabled:bg-slate-300"
        >
          {busy ? "..." : "Tìm"}
        </button>
      </div>
      {searchable && open && (
        <div className="mt-2 max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
          {optionsLoading ? (
            <p className="px-3 py-2 text-xs text-slate-500">Đang tải danh sách ô…</p>
          ) : suggestions.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">
              {options.length === 0 ? "Kho này chưa có ô nào." : "Không có ô nào khớp."}
            </p>
          ) : suggestions.map((b) => (
            <button
              key={b.id}
              type="button"
              // onMouseDown để chọn kịp trước khi ô nhập mất focus.
              onMouseDown={(e) => { e.preventDefault(); void submit(b.code); }}
              disabled={busy}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-2 active:bg-brand-50 ${b.blocked ? "opacity-50" : ""}`}
            >
              <span className="font-mono text-sm text-slate-900 flex-1 min-w-0 truncate">{b.code}</span>
              <span className="text-[11px] text-slate-500 shrink-0 truncate max-w-[45%]">
                {b.zoneName || b.zoneCode}{b.blocked ? ` · ${b.status}` : ""}
              </span>
            </button>
          ))}
          {options.length > suggestions.length && suggestions.length >= MAX_SUGGESTIONS && (
            <p className="px-3 py-1.5 text-[11px] text-slate-400">Gõ thêm để thu hẹp danh sách.</p>
          )}
        </div>
      )}
      {expectedCode && (
        <p className="mt-2 text-xs text-slate-500">
          Ô cần quét: <span className="font-mono text-slate-900">{expectedCode}</span>
        </p>
      )}
      {hint && !error && <p className="mt-2 text-xs text-slate-500">{hint}</p>}
      {error && <p className="mt-2 text-sm font-medium text-red-600">{error}</p>}
    </div>
  );
}
